import React, { useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { RubyGemMark } from '../RubyGemMark';
import { toolsApi, type ToolTenantHit } from '../../services/toolsApi';

const RUBY = '#DC143C';

function Highlight({ text, q }: { text: string; q: string }) {
  const i = q ? text.toLowerCase().indexOf(q.toLowerCase()) : -1;
  if (i < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <b className="text-stone-900">{text.slice(i, i + q.length)}</b>
      {text.slice(i + q.length)}
    </>
  );
}

/**
 * Searchable tenant picker (custom, type-to-filter, debounced) — backed by
 * tools-service `/tools/admin/tenants/search`. Shared by the Tool Library
 * (enable a tool for a tenant) and Data Migration (start a job / grant the
 * client upload page).
 *
 * Tenants in `enabledIds` render greyed with the `enabledLabel` badge and
 * cannot be picked again.
 */
export const TenantPicker: React.FC<{
  enabledIds?: Set<string>;
  onPick: (hit: ToolTenantHit) => Promise<void>;
  onClose: () => void;
  /** Small caption above the search box. */
  title?: string;
  /** Badge on a tenant already in `enabledIds`. */
  enabledLabel?: string;
  /** Render without the tinted panel chrome (e.g. inside a modal). */
  bare?: boolean;
}> = ({ enabledIds, onPick, onClose, title = 'Enable for tenant', enabledLabel = 'ENABLED', bare }) => {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<ToolTenantHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const reqSeq = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const isEnabled = (id: string) => !!enabledIds?.has(id);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    const term = q.trim();
    if (!term) { setHits([]); setSearching(false); setError(null); return; }
    const seq = ++reqSeq.current;
    setSearching(true);
    const t = setTimeout(() => {
      toolsApi.searchTenants(term)
        .then((r) => { if (seq === reqSeq.current) { setHits(r.tenants || []); setError(null); } })
        .catch((e: any) => { if (seq === reqSeq.current) { setHits([]); setError(e?.message || 'Search failed'); } })
        .finally(() => { if (seq === reqSeq.current) setSearching(false); });
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const pick = async (hit: ToolTenantHit) => {
    if (isEnabled(hit.tenantId) || savingId) return;
    setSavingId(hit.tenantId);
    try { await onPick(hit); } finally { setSavingId(null); }
  };

  return (
    <div className={bare ? '' : 'border-t border-stone-100 p-5'} style={bare ? undefined : { background: 'rgba(255,241,243,0.4)' }}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-medium text-stone-500">{title}</div>
        <button type="button" onClick={onClose} className="h-11 w-11 -my-3 -mr-3 grid place-items-center text-stone-400 hover:text-stone-700" aria-label="Close tenant picker">
          <X size={16} />
        </button>
      </div>
      <div
        className="max-w-md bg-white rounded-lg border"
        style={{ borderColor: RUBY, boxShadow: '0 0 0 3px rgba(220,20,60,0.12)' }}
      >
        <div className="h-11 px-3 flex items-center gap-2 border-b border-stone-100">
          <Search size={16} className="text-stone-400 shrink-0" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
            placeholder="Search tenants by name"
            className="flex-1 min-w-0 h-full bg-transparent outline-none text-sm text-stone-800 placeholder:text-stone-400"
          />
          {searching && <span className="text-stone-400"><RubyGemMark size={16} drawing color={RUBY} /></span>}
        </div>
        <div className="py-1 text-sm max-h-72 overflow-y-auto">
          {!q.trim() && <div className="px-3 h-11 flex items-center text-stone-400">Type to search tenants</div>}
          {q.trim() && error && <div className="px-3 h-11 flex items-center text-rose-700">{error}</div>}
          {q.trim() && !error && !searching && hits.length === 0 && (
            <div className="px-3 h-11 flex items-center text-stone-400">No tenants match "{q.trim()}"</div>
          )}
          {hits.map((h) => {
            const already = isEnabled(h.tenantId);
            const saving = savingId === h.tenantId;
            return (
              <button
                key={h.tenantId}
                type="button"
                disabled={already || !!savingId}
                onClick={() => pick(h)}
                className={`w-full px-3 h-11 flex items-center gap-2 text-left ${already ? 'cursor-default' : 'hover:bg-stone-50'} disabled:opacity-100`}
              >
                <span className={`flex-1 truncate ${already ? 'text-stone-400' : 'text-stone-700'}`}>
                  <Highlight text={h.name || h.tenantId} q={q.trim()} />
                </span>
                {saving ? (
                  <span style={{ color: RUBY }}><RubyGemMark size={16} drawing color="currentColor" /></span>
                ) : already ? (
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">{enabledLabel}</span>
                ) : typeof h.locationCount === 'number' ? (
                  <span className="text-xs text-stone-400">{h.locationCount} location{h.locationCount === 1 ? '' : 's'}</span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TenantPicker;
